import { normalizeColumnFilter } from './filterUtils';

export const STORE_SET_FIELDS = new Set(['store', 'store_name']);
export const LOCATION_SET_FIELDS = new Set(['box', 'box_name', 'location_name']);

const uniqueSortedRecords = (rows) => {
  const seen = new Set();
  const out = [];
  (rows || []).forEach((row) => {
    const name = row && row.name != null ? String(row.name).trim() : '';
    const id = row && row.id;
    if (!name || id == null || id === '-1' || seen.has(name + '\0' + id)) return;
    seen.add(name + '\0' + id);
    out.push({ id, name });
  });
  out.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
  return out;
};

export const uniqueNames = (list) => {
  const seen = new Set();
  const names = [];
  (list || []).forEach((value) => {
    const name = value == null ? '' : String(value).trim();
    if (!name || seen.has(name)) return;
    seen.add(name);
    names.push(name);
  });
  names.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  return names;
};

export const namesOf = (records) => uniqueNames((records || []).map((row) => row && row.name));

export const namesFromGrid = (api, kind) => {
  const fields =
    kind === 'store' ? ['store', 'store_name'] : ['box', 'box_name', 'location_name'];
  if (!api || typeof api.forEachNode !== 'function') return [];
  const names = [];
  api.forEachNode((node) => {
    const data = node && node.data;
    if (!data) return;
    fields.forEach((field) => {
      if (data[field] != null) names.push(data[field]);
    });
  });
  return uniqueNames(names);
};

const asArray = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (payload && Array.isArray(payload.Results)) return payload.Results;
  if (payload && Array.isArray(payload.results)) return payload.results;
  return [];
};

const readSessionUser = () => {
  try {
    return JSON.parse(sessionStorage.getItem('user') || 'null');
  } catch (e) {
    return null;
  }
};

const getAuthHeaders = () => {
  const user = readSessionUser();
  const token =
    (user &&
      user.signInUserSession &&
      user.signInUserSession.idToken &&
      user.signInUserSession.idToken.jwtToken) ||
    '';
  return {
    Authorization: 'Bearer ' + token,
    'Content-Type': 'application/json',
  };
};

// Identity of the currently signed-in session. The store/location option cache
// is only valid for this identity; when a different user/account signs in within
// the same SPA lifetime (logout uses client-side navigation, so module state
// survives), the cache must be discarded so the new user never sees the previous
// account's stores/locations.
const getSessionIdentity = () => {
  const user = readSessionUser();
  const payload =
    (user &&
      user.signInUserSession &&
      user.signInUserSession.idToken &&
      user.signInUserSession.idToken.payload) ||
    {};
  const subject = payload.sub || payload['cognito:username'] || (user && user.username) || '';
  if (!subject) return '';
  let parentAccountId = '';
  try {
    parentAccountId = sessionStorage.getItem('parentAccountId') || '';
  } catch (e) {
    parentAccountId = '';
  }
  return String(subject) + '|' + String(parentAccountId);
};

const EMPTY_OPTIONS = { stores: [], locations: [] };

let optionsPromise = null;
let cachedOptions = EMPTY_OPTIONS;
let cachedIdentity = '';

export const clearStoreLocationOptions = () => {
  optionsPromise = null;
  cachedOptions = EMPTY_OPTIONS;
  cachedIdentity = '';
};

const ensureCacheMatchesSession = () => {
  const identity = getSessionIdentity();
  if (identity !== cachedIdentity) {
    clearStoreLocationOptions();
    cachedIdentity = identity;
  }
  return identity;
};

const fetchJson = (url, options) =>
  fetch(url, options).then((response) => {
    if (!response.ok) {
      throw new Error('Failed to load store/location filter values');
    }
    return response.json();
  });

export const loadStoreLocationOptions = () => {
  const identity = ensureCacheMatchesSession();
  if (!identity) {
    // No signed-in session: never serve (or populate) cached options.
    return Promise.resolve(EMPTY_OPTIONS);
  }
  if (cachedOptions.stores.length || cachedOptions.locations.length) {
    return Promise.resolve(cachedOptions);
  }
  if (optionsPromise) return optionsPromise;

  const base = process.env.REACT_APP_BASEURL || '';
  const headers = getAuthHeaders();

  // Only commit results to the cache if the session that started the request is
  // still the active one; otherwise a slow response from user A could land after
  // user B signs in.
  const commit = (options) => {
    if (getSessionIdentity() !== identity) {
      return options;
    }
    cachedOptions = options;
    return cachedOptions;
  };

  const request = fetchJson(base + 'accounts', {
    method: 'GET',
    headers,
  })
    .then((accounts) => {
      const stores = uniqueSortedRecords(asArray(accounts));
      const storeIds = stores.map((a) => a.id);
      if (!storeIds.length) {
        return commit({ stores, locations: [] });
      }
      return fetchJson(base + 'getBoxesByStoreIds', {
        method: 'POST',
        headers,
        body: JSON.stringify({ store_ids: storeIds }),
      }).then((boxes) =>
        commit({
          stores,
          locations: uniqueSortedRecords(asArray(boxes)),
        })
      );
    })
    .catch(() => {
      if (optionsPromise === request) optionsPromise = null;
      return getSessionIdentity() === identity ? cachedOptions : EMPTY_OPTIONS;
    });

  optionsPromise = request;
  return optionsPromise;
};

const columnKey = (colDef) =>
  String((colDef && (colDef.field || colDef.colId)) || '').toLowerCase();

export const getStoreLocationKind = (colDef) => {
  const key = columnKey(colDef);
  if (STORE_SET_FIELDS.has(key)) return 'store';
  if (LOCATION_SET_FIELDS.has(key)) return 'location';
  return null;
};

const rowDataFromFilterParams = (params) =>
  (params && params.data) || (params && params.node && params.node.data) || {};

/**
 * Cell text used by the client-side Store/Location set filter.
 * AG Grid 29 injects valueGetter as `({ node }) => getValue(column, node)` —
 * it must receive `{ node }`, not the row node itself.
 */
export const readStoreLocationCellValue = (params, colDef, valueGetter) => {
  const data = rowDataFromFilterParams(params);
  if (typeof valueGetter === 'function') {
    try {
      const fromGetter = valueGetter({
        node: params && params.node,
        data,
        colDef,
        column: params && params.column,
      });
      if (fromGetter != null && String(fromGetter).trim() !== '') {
        return String(fromGetter).trim();
      }
    } catch (e) {
      // Fall through to the column field on the row.
    }
  }
  const field = colDef && colDef.field;
  if (field && data[field] != null) {
    return String(data[field]).trim();
  }
  return '';
};

export const doesStoreLocationValueMatch = (selected, value) => {
  if (!selected || !selected.length) return true;
  const name = value == null ? '' : String(value).trim().toLowerCase();
  return selected.some((s) => String(s).trim().toLowerCase() === name);
};

export const idsForSelectedNames = (kind, names) => {
  ensureCacheMatchesSession();
  const list = kind === 'store' ? cachedOptions.stores : cachedOptions.locations;
  const want = new Set((names || []).map((n) => String(n).trim()).filter(Boolean));
  if (!want.size) return [];
  return list.filter((row) => want.has(row.name)).map((row) => row.id);
};

export const applyStoreLocationIdsFromFilterModel = (body, filterModel) => {
  if (!body) return body;
  const nextStoreIds = [];
  const nextBoxIds = [];
  const model = filterModel || {};
  Object.keys(model).forEach((colId) => {
    const kind = getStoreLocationKind({ field: colId, colId });
    if (!kind) return;
    const normalized = normalizeColumnFilter(model[colId]);
    const names = normalized && normalized.values;
    if (!names || !names.length) return;
    const ids = idsForSelectedNames(kind, names);
    if (kind === 'store') {
      ids.forEach((id) => {
        if (!nextStoreIds.includes(id)) nextStoreIds.push(id);
      });
    } else {
      ids.forEach((id) => {
        if (!nextBoxIds.includes(id)) nextBoxIds.push(id);
      });
    }
  });
  if (Array.isArray(body.store_ids) || nextStoreIds.length) {
    body.store_ids = nextStoreIds;
  }
  if (Array.isArray(body.box_ids) || nextBoxIds.length) {
    body.box_ids = nextBoxIds;
  }
  return body;
};



// WEBPACK FOOTER //
// ./src/components/grid/storeLocationFilter.js